import React, { useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { heroTags, serviceTypes } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { createConsultation } from '../api/easyworks';
import { validateConsultation, hasErrors } from '../lib/validators';

const Hero = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ company: '', name: '', phone: '', email: '', service: 'VPOB & APOB', ecom: false, website: '' });
  const [errors, setErrors] = useState({});
  const [serviceOpen, setServiceOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateConsultation(form);
    if (hasErrors(found)) {
      setErrors(found);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await createConsultation({
        company: form.company,
        name: form.name,
        phone: form.phone,
        email: form.email,
        service: form.service,
        isEcommerce: form.ecom,
        source: 'hero',
        website: form.website,
      });
      toast({ title: 'Consultation requested!', description: 'Our expert will reach out within 24 hours.' });
      setForm({ company: '', name: '', phone: '', email: '', service: 'VPOB & APOB', ecom: false, website: '' });
      setErrors({});
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Please try again.';
      toast({ title: 'Submission failed', description: typeof msg === 'string' ? msg : 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative bg-[#FAF2E8] overflow-hidden">
      {/* dotted decorations */}
      <div className="absolute right-10 bottom-32 grid grid-cols-8 gap-2 opacity-40 pointer-events-none">
        {Array.from({ length: 64 }).map((_, i) => (
          <span key={i} className="w-1 h-1 rounded-full bg-[#F39200]/50" />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-10 lg:pt-16 pb-10">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left */}
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-neutral-900 leading-[1.1]">
              All your compliances made{' '}
              <span className="relative inline-block">
                <span>super easy</span>
                <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 300 10" fill="none">
                  <path d="M2 7 C 80 1, 160 1, 298 6" stroke="#111" strokeWidth="2" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h1>
            <p className="mt-6 text-[15px] md:text-base text-neutral-600 max-w-xl leading-relaxed">
              Welcome to the realm of VPOB and APOB &mdash; where the magic of virtual office and accounting services intertwine to revolutionise your business journey!
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {heroTags.map((t) => (
                <span key={t} className="px-3.5 py-1.5 text-[13px] text-neutral-800 bg-white border border-neutral-200 rounded-full hover:border-[#F39200] hover:text-[#F39200] transition-colors cursor-pointer">
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Right - illustration */}
          <div className="relative">
            <div className="relative aspect-[4/3] w-full max-w-xl ml-auto">
              <div className="absolute inset-0 rounded-full border border-[#F39200]/30" />
              <div className="absolute inset-6 rounded-full border border-[#F39200]/20" />
              <div className="absolute inset-12 rounded-full border border-[#F39200]/10" />
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=900&q=80"
                alt="team"
                className="absolute inset-x-6 bottom-0 top-10 w-[calc(100%-3rem)] h-[calc(100%-2.5rem)] object-cover rounded-2xl"
              />
              <span className="absolute top-6 left-6 px-4 py-2 bg-white shadow-md rounded-full text-sm font-medium text-[#F39200]">Live App Access</span>
              <span className="absolute top-2 right-10 px-4 py-2 bg-white shadow-md rounded-full text-sm font-medium text-[#F39200]">Status Tracking</span>
              <span className="absolute top-24 right-2 px-4 py-2 bg-white shadow-md rounded-full text-sm font-medium text-neutral-900">Dashboard Access</span>
            </div>
          </div>
        </div>

        {/* Consultation form card */}
        <form onSubmit={submit} className="mt-10 bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(0,0,0,0.08)] p-5 md:p-6 border border-black/5">
          <div className="text-[15px] md:text-base font-medium text-neutral-900">
            Get your <span className="text-[#F39200] font-semibold">free consultation</span> by expert
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-start">
            {/* Honeypot */}
            <input type="text" name="website" tabIndex="-1" autoComplete="off" aria-hidden="true"
              value={form.website} onChange={(e)=>update('website', e.target.value)}
              style={{ position:'absolute', left:'-10000px', width:'1px', height:'1px', opacity:0 }} />
            <div className="lg:col-span-1">
              <input
                value={form.company}
                onChange={(e)=>update('company', e.target.value)}
                data-testid="hero-input-company"
                className="w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border border-transparent focus:border-[#F39200] focus:bg-white outline-none text-sm"
                placeholder="Company name"
              />
            </div>
            <div className="lg:col-span-1">
              <input
                value={form.name}
                onChange={(e)=>update('name', e.target.value)}
                aria-invalid={!!errors.name}
                data-testid="hero-input-name"
                className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.name ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
                placeholder="Your name *"
              />
              {errors.name && <p data-testid="hero-error-name" className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>
            <div className="lg:col-span-1">
              <input
                value={form.phone}
                onChange={(e)=>update('phone', e.target.value)}
                aria-invalid={!!errors.phone}
                data-testid="hero-input-phone"
                className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.phone ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
                placeholder="Phone Number *"
              />
              {errors.phone && <p data-testid="hero-error-phone" className="mt-1 text-xs text-red-600">{errors.phone}</p>}
            </div>
            <div className="lg:col-span-1">
              <input
                value={form.email}
                onChange={(e)=>update('email', e.target.value)}
                aria-invalid={!!errors.email}
                data-testid="hero-input-email"
                className={`w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border focus:bg-white outline-none text-sm ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-transparent focus:border-[#F39200]'}`}
                placeholder="Email ID"
              />
              {errors.email && <p data-testid="hero-error-email" className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>
            <div className="relative lg:col-span-1">
              <button type="button" onClick={()=>setServiceOpen(!serviceOpen)} className="w-full h-11 px-3.5 rounded-lg bg-[#F7F3EC] border border-transparent hover:border-[#F39200] flex items-center justify-between text-sm text-neutral-800">
                {form.service}
                <ChevronDown size={16} className={`transition-transform ${serviceOpen ? 'rotate-180' : ''}`} />
              </button>
              {serviceOpen && (
                <ul className="absolute z-30 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden">
                  {serviceTypes.map((s) => (
                    <li key={s}>
                      <button type="button" onClick={()=>{ setForm({...form, service:s}); setServiceOpen(false); }} className="w-full text-left px-3.5 py-2 text-sm hover:bg-[#FAF2E8]">{s}</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button type="submit" disabled={submitting} data-testid="hero-submit-btn" className="lg:col-span-1 h-11 rounded-lg bg-neutral-900 hover:bg-neutral-800 disabled:opacity-60 text-white flex items-center justify-center gap-2 text-sm font-medium transition-colors">
              {submitting ? 'Submitting...' : (<>Submit <ArrowRight size={16} /></>)}
            </button>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm text-neutral-700 cursor-pointer w-fit">
            <input type="checkbox" checked={form.ecom} onChange={(e)=>setForm({...form, ecom:e.target.checked})} className="accent-[#F39200] w-4 h-4" />
            Are you an E-Commerce seller?
          </label>
        </form>
      </div>
    </section>
  );
};

export default Hero;
